/**
 * ESG SaaS Platform - Emission Factor Service
 *
 * Provides API integration for emission factor lookups and management.
 * Supports DEFRA 2024 factors with dynamic fuel type and unit selection.
 */

import { api } from './api';

// Types for emission factors
export interface EmissionFactor {
  id: string;
  source: string;
  scope: number;
  category: string;
  activity: string;
  fuel_type?: string;
  vehicle_type?: string;
  refrigerant?: string;
  refrigerant_type?: string;
  region?: string;
  value: number;
  unit_input: string;
  unit_output: string;
  co2_value?: number;
  ch4_value?: number;
  n2o_value?: number;
  gwp?: number;
  is_custom: boolean;
  organization_id?: string;
  override_reason?: string;
  evidence_url?: string;
}

export interface EmissionFactorLookupRequest {
  scope: number;
  category?: string;
  activity?: string;
  fuel_type?: string;
  vehicle_type?: string;
  region?: string;
  unit?: string;  // Backend expects 'unit', not 'unit_input'
  organization_id?: string;
}

export interface EmissionFactorLookupResponse {
  found: boolean;
  factor: EmissionFactor | null;
  alternatives?: EmissionFactor[];
  message?: string;
}

export interface EmissionFactorListResponse {
  factors: EmissionFactor[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Scope 1 Categories with DEFRA fuel types
export interface FuelTypeOption {
  label: string;
  value: string;
  units: string[];
  defaultUnit: string;
}

export interface ActivityOption {
  label: string;
  value: string;
  fuelTypes: FuelTypeOption[];
}

export interface Scope1CategoryOption {
  label: string;
  value: string;
  activities: ActivityOption[];
}

// DEFRA 2024 Scope 1 fuel types and units
export const SCOPE1_CATEGORIES: Scope1CategoryOption[] = [
  {
    label: 'Stationary Combustion',
    value: 'stationary_combustion',
    activities: [
      {
        label: 'Gaseous Fuels',
        value: 'gaseous_fuels',
        fuelTypes: [
          { label: 'Natural Gas', value: 'Natural gas', units: ['tonnes', 'cubic metres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'cubic metres' },
          { label: 'Natural Gas (100% Mineral)', value: 'Natural gas (100% mineral blend)', units: ['tonnes', 'cubic metres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'cubic metres' },
          { label: 'LPG', value: 'LPG', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Propane', value: 'Propane', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Butane', value: 'Butane', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'CNG', value: 'CNG', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'LNG', value: 'LNG', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Other Petroleum Gas', value: 'Other petroleum gas', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
        ],
      },
      {
        label: 'Liquid Fuels',
        value: 'liquid_fuels',
        fuelTypes: [
          { label: 'Diesel (Average Biofuel Blend)', value: 'Diesel (average biofuel blend)', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Diesel (100% Mineral)', value: 'Diesel (100% mineral diesel)', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Petrol (Average Biofuel Blend)', value: 'Petrol (average biofuel blend)', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Petrol (100% Mineral)', value: 'Petrol (100% mineral petrol)', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Burning Oil', value: 'Burning oil', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Gas Oil', value: 'Gas oil', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Fuel Oil', value: 'Fuel oil', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Marine Fuel Oil', value: 'Marine fuel oil', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Marine Gas Oil', value: 'Marine gas oil', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Aviation Spirit', value: 'Aviation spirit', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Aviation Turbine Fuel', value: 'Aviation turbine fuel', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Naphtha', value: 'Naphtha', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Lubricants', value: 'Lubricants', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
          { label: 'Waste Oils', value: 'Waste oils', units: ['tonnes', 'litres', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'litres' },
        ],
      },
      {
        label: 'Solid Fuels',
        value: 'solid_fuels',
        fuelTypes: [
          { label: 'Coal (Industrial)', value: 'Coal (industrial)', units: ['tonnes', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'tonnes' },
          { label: 'Coal (Domestic)', value: 'Coal (domestic)', units: ['tonnes', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'tonnes' },
          { label: 'Coal (Electricity Generation)', value: 'Coal (electricity generation)', units: ['tonnes', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'tonnes' },
          { label: 'Coking Coal', value: 'Coking coal', units: ['tonnes', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'tonnes' },
          { label: 'Petroleum Coke', value: 'Petroleum coke', units: ['tonnes', 'kWh (Net CV)', 'kWh (Gross CV)'], defaultUnit: 'tonnes' },
        ],
      },
    ],
  },
  {
    label: 'Mobile Combustion',
    value: 'mobile_combustion',
    // EPA 2024 fuel-based emission factors
    // Source: EPA GHG Emission Factors Hub 2024 - Table 2: Mobile Combustion CO2
    // https://www.epa.gov/climateleadership/ghg-emission-factors-hub
    activities: [
      {
        label: 'On-Road Vehicles',
        value: 'on_road',
        fuelTypes: [
          // EPA 2024: Motor Gasoline - 8.78 kg CO2/gallon = 2.348 kg CO2e/litre
          { label: 'Motor Gasoline (Petrol)', value: 'Motor Gasoline', units: ['litres', 'gallons'], defaultUnit: 'litres' },
          // EPA 2024: Diesel - 10.21 kg CO2/gallon = 2.697 kg CO2e/litre
          { label: 'Diesel', value: 'Diesel', units: ['litres', 'gallons'], defaultUnit: 'litres' },
          // EPA 2024: Biodiesel (B100) - 9.45 kg CO2/gallon = 2.497 kg CO2e/litre (biogenic)
          { label: 'Biodiesel (B100)', value: 'Biodiesel (B100)', units: ['litres', 'gallons'], defaultUnit: 'litres' },
          // EPA 2024: Ethanol (E100) - 5.75 kg CO2/gallon = 1.519 kg CO2e/litre (biogenic)
          { label: 'Ethanol (E100)', value: 'Ethanol (E100)', units: ['litres', 'gallons'], defaultUnit: 'litres' },
          // EPA 2024: LPG - 5.68 kg CO2/gallon = 1.501 kg CO2e/litre
          { label: 'LPG (Propane)', value: 'LPG', units: ['litres', 'gallons'], defaultUnit: 'litres' },
          // EPA 2024: CNG - 0.05444 kg CO2/scf = 2.743 kg CO2e/kg
          { label: 'CNG (Compressed Natural Gas)', value: 'CNG', units: ['kg', 'scf'], defaultUnit: 'kg' },
          // EPA 2024: LNG - 4.50 kg CO2/gallon = 1.189 kg CO2e/litre
          { label: 'LNG (Liquefied Natural Gas)', value: 'LNG', units: ['litres', 'gallons'], defaultUnit: 'litres' },
        ],
      },
      {
        label: 'Off-Road Equipment',
        value: 'off_road',
        fuelTypes: [
          // Same EPA factors for off-road diesel/gasoline consumption
          { label: 'Diesel (Off-Road)', value: 'Diesel (Off-Road)', units: ['litres', 'gallons'], defaultUnit: 'litres' },
          { label: 'Gasoline (Off-Road)', value: 'Gasoline (Off-Road)', units: ['litres', 'gallons'], defaultUnit: 'litres' },
        ],
      },
      {
        label: 'Marine Vessels',
        value: 'marine',
        fuelTypes: [
          // EPA 2024: Residual Fuel Oil - 11.27 kg CO2/gallon = 2.977 kg CO2e/litre
          { label: 'Residual Fuel Oil (HFO)', value: 'Residual Fuel Oil', units: ['litres', 'gallons'], defaultUnit: 'litres' },
          // EPA 2024: Marine Diesel - 10.21 kg CO2/gallon = 2.697 kg CO2e/litre
          { label: 'Marine Diesel', value: 'Marine Diesel', units: ['litres', 'gallons'], defaultUnit: 'litres' },
        ],
      },
      {
        label: 'Aviation',
        value: 'aviation',
        fuelTypes: [
          // EPA 2024: Jet Fuel - 9.75 kg CO2/gallon = 2.575 kg CO2e/litre
          { label: 'Jet Fuel (Kerosene)', value: 'Jet Fuel (Kerosene)', units: ['litres', 'gallons'], defaultUnit: 'litres' },
          // EPA 2024: Aviation Gasoline - 8.31 kg CO2/gallon = 2.195 kg CO2e/litre
          { label: 'Aviation Gasoline (Avgas)', value: 'Aviation Gasoline', units: ['litres', 'gallons'], defaultUnit: 'litres' },
        ],
      },
    ],
  },
  {
    label: 'Fugitive Emissions',
    value: 'fugitive_emissions',
    activities: [
      {
        label: 'Refrigerants',
        value: 'refrigerant',
        fuelTypes: [
          // ===== KYOTO PROTOCOL HFCs =====
          { label: 'HFC-23 (R-23) - GWP 12400', value: 'HFC-23', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-32 (R-32) - GWP 677', value: 'HFC-32', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-41 - GWP 116', value: 'HFC-41', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-125 (R-125) - GWP 3170', value: 'HFC-125', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-134 - GWP 1120', value: 'HFC-134', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-134a (R-134a) - GWP 1300', value: 'HFC-134a', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-143 - GWP 328', value: 'HFC-143', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-143a (R-143a) - GWP 4800', value: 'HFC-143a', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-152 - GWP 16', value: 'HFC-152', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-152a (R-152a) - GWP 138', value: 'HFC-152a', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-161 - GWP 4', value: 'HFC-161', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-227ea (R-227ea) - GWP 3350', value: 'HFC-227ea', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-236cb - GWP 1210', value: 'HFC-236cb', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-236ea - GWP 1330', value: 'HFC-236ea', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-236fa (R-236fa) - GWP 8060', value: 'HFC-236fa', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-245ca - GWP 716', value: 'HFC-245ca', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-245fa - GWP 858', value: 'HFC-245fa', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-365mfc - GWP 804', value: 'HFC-365mfc', units: ['kg'], defaultUnit: 'kg' },
          { label: 'HFC-43-10mee - GWP 1650', value: 'HFC-43-I0mee', units: ['kg'], defaultUnit: 'kg' },

          // ===== COMMON REFRIGERANT BLENDS (R-400 SERIES) =====
          { label: 'R-401A - GWP 18', value: 'R401A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-401B - GWP 15', value: 'R401B', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-401C - GWP 21', value: 'R401C', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-402A - GWP 1902', value: 'R402A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-402B - GWP 1205', value: 'R402B', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-403A - GWP 1780', value: 'R403A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-403B - GWP 3471', value: 'R403B', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-404A - GWP 3943', value: 'R404A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-405A - GWP 3920', value: 'R405A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-407A - GWP 1923', value: 'R407A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-407B - GWP 2547', value: 'R407B', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-407C - GWP 1624', value: 'R407C', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-407D - GWP 1487', value: 'R407D', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-407E - GWP 1425', value: 'R407E', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-407F - GWP 1674', value: 'R407F', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-408A - GWP 2430', value: 'R408A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-410A - GWP 1924', value: 'R410A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-410B - GWP 2048', value: 'R410B', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-411A - GWP 15', value: 'R411A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-411B - GWP 4', value: 'R411B', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-412A - GWP 445', value: 'R412A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-413A - GWP 1945', value: 'R413A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-415A - GWP 25', value: 'R415A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-415B - GWP 104', value: 'R415B', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-416A - GWP 767', value: 'R416A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-417A - GWP 2127', value: 'R417A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-417B - GWP 2742', value: 'R417B', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-417C - GWP 1643', value: 'R417C', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-418A - GWP 3', value: 'R418A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-419A - GWP 2688', value: 'R419A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-419B - GWP 2161', value: 'R419B', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-420A - GWP 1144', value: 'R420A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-421A - GWP 2385', value: 'R421A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-421B - GWP 2890', value: 'R421B', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-422A - GWP 2847', value: 'R422A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-422B - GWP 2290', value: 'R422B', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-422C - GWP 2794', value: 'R422C', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-422D - GWP 2473', value: 'R422D', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-422E - GWP 2350', value: 'R422E', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-423A - GWP 2274', value: 'R423A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-424A - GWP 2212', value: 'R424A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-425A - GWP 1431', value: 'R425A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-426A - GWP 1371', value: 'R426A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-427A - GWP 2024', value: 'R427A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-428A - GWP 3417', value: 'R428A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-429A - GWP 14', value: 'R429A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-430A - GWP 105', value: 'R430A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-431A - GWP 40', value: 'R431A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-434A - GWP 3075', value: 'R434A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-435A - GWP 28', value: 'R435A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-437A - GWP 1639', value: 'R437A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-438A - GWP 2059', value: 'R438A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-439A - GWP 1828', value: 'R439A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-440A - GWP 156', value: 'R440A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-442A - GWP 1754', value: 'R442A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-444A - GWP 88', value: 'R444A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-445A - GWP 117', value: 'R445A', units: ['kg'], defaultUnit: 'kg' },

          // ===== R-500 SERIES BLENDS =====
          { label: 'R-500 - GWP 36', value: 'R500', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-503 - GWP 4972', value: 'R503', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-504 - GWP 326', value: 'R504', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-507A - GWP 3985', value: 'R507A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-508A - GWP 11607', value: 'R508A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-508B - GWP 11698', value: 'R508B', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-509A - GWP 4984', value: 'R509A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-511A - GWP 7', value: 'R511A', units: ['kg'], defaultUnit: 'kg' },
          { label: 'R-512A - GWP 196', value: 'R512A', units: ['kg'], defaultUnit: 'kg' },

          // ===== PFCs (PERFLUOROCARBONS) =====
          { label: 'PFC-14 / CF4 (Perfluoromethane) - GWP 6630', value: 'Perfluoromethane (PFC-14)', units: ['kg'], defaultUnit: 'kg' },
          { label: 'PFC-116 / C2F6 (Perfluoroethane) - GWP 11100', value: 'Perfluoroethane (PFC-116)', units: ['kg'], defaultUnit: 'kg' },
          { label: 'PFC-218 / C3F8 (Perfluoropropane) - GWP 8900', value: 'Perfluoropropane (PFC-218)', units: ['kg'], defaultUnit: 'kg' },
          { label: 'PFC-318 (Perfluorocyclobutane) - GWP 9540', value: 'Perfluorocyclobutane (PFC-318)', units: ['kg'], defaultUnit: 'kg' },
          { label: 'PFC-3-1-10 (Perfluorobutane) - GWP 9200', value: 'Perfluorobutane (PFC-3-1-10)', units: ['kg'], defaultUnit: 'kg' },
          { label: 'PFC-4-1-12 (Perfluoropentane) - GWP 8550', value: 'Perfluoropentane (PFC-4-1-12)', units: ['kg'], defaultUnit: 'kg' },
          { label: 'PFC-5-1-14 (Perfluorohexane) - GWP 7910', value: 'Perfluorohexane (PFC-5-1-14)', units: ['kg'], defaultUnit: 'kg' },
          { label: 'PFC-9-1-18 - GWP 7190', value: 'PFC-9-1-18', units: ['kg'], defaultUnit: 'kg' },
          { label: 'Perfluorocyclopropane - GWP 9200', value: 'Perfluorocyclopropane', units: ['kg'], defaultUnit: 'kg' },

          // ===== OTHER KYOTO PROTOCOL GASES =====
          { label: 'Carbon dioxide (CO2) - GWP 1', value: 'Carbon dioxide', units: ['kg'], defaultUnit: 'kg' },
          { label: 'Methane (CH4) - GWP 28', value: 'Methane', units: ['kg'], defaultUnit: 'kg' },
          { label: 'Nitrous oxide (N2O) - GWP 265', value: 'Nitrous oxide', units: ['kg'], defaultUnit: 'kg' },
          { label: 'Sulphur hexafluoride (SF6) - GWP 23500', value: 'Sulphur hexafluoride (SF6)', units: ['kg'], defaultUnit: 'kg' },
          { label: 'Nitrogen trifluoride (NF3) - GWP 16100', value: 'Nitrogen trifluoride', units: ['kg'], defaultUnit: 'kg' },
        ],
      },
    ],
  },
  {
    label: 'Process Emissions',
    value: 'process_emissions',
    activities: [
      {
        label: 'Industrial Processes',
        value: 'industrial_processes',
        fuelTypes: [
          { label: 'Chemical Reactions', value: 'Chemical reactions', units: ['kg CO2e'], defaultUnit: 'kg CO2e' },
          { label: 'Metal Production', value: 'Metal production', units: ['kg CO2e'], defaultUnit: 'kg CO2e' },
          { label: 'Mineral Processing', value: 'Mineral processing', units: ['kg CO2e'], defaultUnit: 'kg CO2e' },
        ],
      },
    ],
  },
];

// Scope 2 energy types
export interface Scope2EnergyType {
  label: string;
  value: string;
  unit: string;
  defaultFactorLocationBased: number;
  defaultFactorMarketBased: number;
}

export const SCOPE2_ENERGY_TYPES: Scope2EnergyType[] = [
  { label: 'Electricity', value: 'electricity', unit: 'kWh', defaultFactorLocationBased: 0.21233, defaultFactorMarketBased: 0.21233 },
  { label: 'Heat', value: 'heat', unit: 'kWh', defaultFactorLocationBased: 0.17048, defaultFactorMarketBased: 0.17048 },
  { label: 'Steam', value: 'steam', unit: 'kWh', defaultFactorLocationBased: 0.17048, defaultFactorMarketBased: 0.17048 },
  { label: 'Cooling', value: 'cooling', unit: 'kWh', defaultFactorLocationBased: 0.21233, defaultFactorMarketBased: 0.21233 },
];

// Service functions
export const emissionFactorService = {
  /**
   * List emission factors with optional filters
   */
  listFactors: async (params: {
    scope?: number;
    category?: string;
    source?: string;
    page?: number;
    page_size?: number;
  }): Promise<EmissionFactorListResponse> => {
    const queryParams = new URLSearchParams();
    if (params.scope) queryParams.append('scope', params.scope.toString());
    if (params.category) queryParams.append('category', params.category);
    if (params.source) queryParams.append('source', params.source);

    // Convert page-based pagination to offset-based (backend uses limit/offset)
    const limit = params.page_size || 50;
    const offset = params.page ? (params.page - 1) * limit : 0;
    queryParams.append('limit', limit.toString());
    queryParams.append('offset', offset.toString());

    return api.get<EmissionFactorListResponse>(`/emission-factors?${queryParams.toString()}`);
  },

  /**
   * Look up a specific emission factor
   */
  lookupFactor: async (request: EmissionFactorLookupRequest): Promise<EmissionFactorLookupResponse> => {
    return api.post<EmissionFactorLookupResponse>('/emission-factors/lookup', request);
  },

  /**
   * Get emission factor value for frontend calculation preview
   */
  getFactorValue: async (
    scope: number,
    category: string,
    activity: string,
    fuelType: string,
    unit: string,
    organizationId?: string
  ): Promise<{ value: number; source: string }> => {
    try {
      const response = await emissionFactorService.lookupFactor({
        scope,
        category,
        activity,
        fuel_type: fuelType,
        unit: unit,  // Backend expects 'unit', not 'unit_input'
        organization_id: organizationId,
      });

      // Extract value from nested factor object
      if (response.found && response.factor) {
        // Default source based on category
        const defaultSource = category === 'mobile_combustion' ? 'EPA 2024' : 'DEFRA 2024';
        return {
          value: response.factor.value,
          source: response.factor.source || defaultSource,
        };
      }

      // If no exact match, try to find alternative with matching unit
      if (response.alternatives && response.alternatives.length > 0) {
        // First try to find an alternative with the exact unit
        const unitMatchedAlt = response.alternatives.find(
          alt => alt.unit_input?.toLowerCase() === unit.toLowerCase()
        );
        if (unitMatchedAlt) {
          const defaultSource = category === 'mobile_combustion' ? 'EPA 2024' : 'DEFRA 2024';
          return {
            value: unitMatchedAlt.value,
            source: `${unitMatchedAlt.source || defaultSource} (similar match)`,
          };
        }
        // If no unit match, fall through to fallback
      }

      // No match found - use fallback with unit conversion
      console.warn('No emission factor found in database, using fallback');
      return {
        value: getFallbackFactorWithUnit(scope, category, fuelType, unit),
        source: 'Fallback (default)',
      };
    } catch (error) {
      // Return fallback values if API fails
      console.warn('Failed to fetch emission factor, using fallback:', error);
      return {
        value: getFallbackFactorWithUnit(scope, category, fuelType, unit),
        source: 'Fallback (offline)',
      };
    }
  },

  /**
   * Import DEFRA factors into database (admin)
   */
  importDefraFactors: async (): Promise<{ total_imported: number; by_category: Record<string, number>; source: string; version: string }> => {
    return api.post('/emission-factors/import/defra');
  },

  /**
   * Import EPA Mobile Combustion factors into database (admin)
   * Source: EPA GHG Emission Factors Hub 2024
   */
  importEpaMobileFactors: async (): Promise<{ total_imported: number; by_category: Record<string, number>; source: string; version: string }> => {
    return api.post('/emission-factors/import/epa-mobile');
  },

  /**
   * Create a custom emission factor
   */
  createCustomFactor: async (factor: Partial<EmissionFactor>): Promise<EmissionFactor> => {
    return api.post<EmissionFactor>('/emission-factors', factor);
  },

  /**
   * Update an emission factor (admin)
   */
  updateFactor: async (factorId: string, factor: Partial<EmissionFactor>): Promise<EmissionFactor> => {
    return api.put<EmissionFactor>(`/emission-factors/${factorId}`, factor);
  },

  /**
   * Delete an emission factor (admin)
   */
  deleteFactor: async (factorId: string): Promise<{ id: string; deleted: boolean }> => {
    return api.delete<{ id: string; deleted: boolean }>(`/emission-factors/${factorId}`);
  },

  /**
   * Delete all emission factors for a specific scope (admin)
   */
  deleteFactorsByScope: async (scope: number): Promise<{ scope: number; deleted_count: number; message: string }> => {
    return api.delete<{ scope: number; deleted_count: number; message: string }>(`/emission-factors/by-scope/${scope}`);
  },
};

// Fallback factors for offline mode (kg CO2e per unit specified)
// Organized by category to avoid key conflicts between stationary and mobile combustion
function getFallbackFactor(scope: number, category: string, fuelType: string): number {
  // ===== MOBILE COMBUSTION - EPA 2024 FUEL-BASED (kg CO2e per litre) =====
  // Source: EPA GHG Emission Factors Hub 2024 - Table 2: Mobile Combustion CO2
  // https://www.epa.gov/climateleadership/ghg-emission-factors-hub
  if (category === 'mobile_combustion') {
    const mobileFallbacks: Record<string, number> = {
      // On-Road Vehicles (per litre)
      'Motor Gasoline': 2.348,        // 8.78 kg CO2/gallon converted
      'Diesel': 2.697,                // 10.21 kg CO2/gallon converted
      'Biodiesel (B100)': 2.497,      // 9.45 kg CO2/gallon converted (biogenic)
      'Ethanol (E100)': 1.519,        // 5.75 kg CO2/gallon converted (biogenic)
      'LPG': 1.501,                   // 5.68 kg CO2/gallon converted
      'CNG': 2.743,                   // per kg (0.05444 kg CO2/scf converted)
      'LNG': 1.189,                   // 4.50 kg CO2/gallon converted
      // Off-Road Equipment (per litre)
      'Diesel (Off-Road)': 2.697,     // Same as on-road diesel
      'Gasoline (Off-Road)': 2.348,   // Same as on-road gasoline
      // Marine (per litre)
      'Residual Fuel Oil': 2.977,     // 11.27 kg CO2/gallon converted
      'Marine Diesel': 2.697,         // Same as on-road diesel
      // Aviation (per litre)
      'Jet Fuel (Kerosene)': 2.575,   // 9.75 kg CO2/gallon converted
      'Aviation Gasoline': 2.195,     // 8.31 kg CO2/gallon converted
    };

    if (mobileFallbacks[fuelType]) {
      return mobileFallbacks[fuelType];
    }

    // Try partial match for mobile combustion
    const lowerFuelType = fuelType.toLowerCase();
    for (const [key, value] of Object.entries(mobileFallbacks)) {
      if (key.toLowerCase().includes(lowerFuelType) || lowerFuelType.includes(key.toLowerCase())) {
        return value;
      }
    }

    return 2.5; // Default for unknown mobile fuel
  }

  // ===== STATIONARY COMBUSTION & OTHER CATEGORIES (DEFRA 2024) =====
  const fallbacks: Record<string, number> = {
    // Gaseous fuels (kg CO2e per cubic metre unless specified)
    'Natural gas': 2.04542,
    'LPG': 1.55713,
    'Propane': 1.54357,
    'Butane': 1.74532,
    'CNG': 0.44942,
    'LNG': 1.17216,
    // Liquid fuels (kg CO2e per litre)
    'Diesel (average biofuel blend)': 2.51233,
    'Diesel (100% mineral diesel)': 2.70405,
    'Petrol (average biofuel blend)': 2.16419,
    'Petrol (100% mineral petrol)': 2.34156,
    'Burning oil': 2.54319,
    'Gas oil': 2.76099,
    'Fuel oil': 3.22836,
    // Solid fuels (kg CO2e per tonne)
    'Coal (industrial)': 2391.58,
    'Coal (domestic)': 2883.26,

    // ===== FUGITIVE EMISSIONS - REFRIGERANTS (GWP - kg CO2e per kg) =====
    'HFC-134a': 1300,
    'R404A': 3943,
    'R407C': 1624,
    'R410A': 1924,

    // ===== SCOPE 2 - ELECTRICITY & HEAT (kg CO2e per kWh - UK 2024) =====
    'electricity': 0.21233,
    'heat': 0.17048,
    'steam': 0.17048,
  };

  // Try exact match first
  if (fallbacks[fuelType]) {
    return fallbacks[fuelType];
  }

  // Try partial match (case-insensitive)
  const lowerFuelType = fuelType.toLowerCase();
  for (const [key, value] of Object.entries(fallbacks)) {
    if (key.toLowerCase().includes(lowerFuelType) || lowerFuelType.includes(key.toLowerCase())) {
      return value;
    }
  }

  return 2.0; // Default fallback
}

// Fallback factors with unit support for mobile combustion
// EPA 2024 fuel-based emission factors with unit conversion
// Base factors are per litre (metric), convert to gallons (US) when needed
function getFallbackFactorWithUnit(scope: number, category: string, fuelType: string, unit: string): number {
  // Get base factor (EPA 2024: per litre for liquids, per kg for CNG)
  const baseFactor = getFallbackFactor(scope, category, fuelType);

  // For mobile combustion, convert based on unit
  if (category === 'mobile_combustion') {
    const unitLower = unit.toLowerCase();

    // Litre to gallon conversion: 1 gallon = 3.78541 litres
    // So: factor per gallon = factor per litre * 3.78541
    if (unitLower === 'gallons' || unitLower === 'gallon') {
      // CNG uses kg/scf, not litres/gallons
      if (fuelType === 'CNG') {
        // CNG: 0.05444 kg CO2/scf (EPA direct value per scf)
        return 0.05444;
      }
      // For liquid fuels, convert from per-litre to per-gallon
      return baseFactor * 3.78541;
    }

    // For CNG with scf (standard cubic feet)
    if (unitLower === 'scf') {
      return 0.05444; // EPA 2024 direct value per scf
    }

    // For kg (CNG), return the base factor which is already per kg
    if (unitLower === 'kg') {
      return baseFactor; // 2.743 kg CO2e per kg CNG
    }

    // Default: return litre-based factor
    return baseFactor;
  }

  // For non-mobile combustion, return base factor
  return baseFactor;
}

// Helper to get display label for a category
export function getCategoryLabel(categoryValue: string): string {
  for (const cat of SCOPE1_CATEGORIES) {
    if (cat.value === categoryValue) return cat.label;
  }
  return categoryValue;
}

// Helper to get display label for an activity
export function getActivityLabel(categoryValue: string, activityValue: string): string {
  const category = SCOPE1_CATEGORIES.find(c => c.value === categoryValue);
  if (category) {
    const activity = category.activities.find(a => a.value === activityValue);
    if (activity) return activity.label;
  }
  return activityValue;
}

// Helper to get fuel types for a category and activity
export function getFuelTypesForActivity(categoryValue: string, activityValue: string): FuelTypeOption[] {
  const category = SCOPE1_CATEGORIES.find(c => c.value === categoryValue);
  if (category) {
    const activity = category.activities.find(a => a.value === activityValue);
    if (activity) return activity.fuelTypes;
  }
  return [];
}

// Helper to get available units for a fuel type
export function getUnitsForFuelType(categoryValue: string, activityValue: string, fuelTypeValue: string): string[] {
  const fuelTypes = getFuelTypesForActivity(categoryValue, activityValue);
  const fuelType = fuelTypes.find(f => f.value === fuelTypeValue);
  return fuelType?.units || [];
}

export default emissionFactorService;
